import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import {
  countOtherLeadsByClientPhone,
  listOtherLeadsByClientPhone,
  listSameAdLeads,
  type OtherLeadRow,
} from '@/api/inspection-leads'
import type { InspectionLeadRow } from '@/types/inspection-lead'
import type { OrderStatus } from '@/types/enums'
import { statusLabelRu } from '@/utils/status-transitions'
import { formatMoscowDateTime } from '@/utils/format'

const PAGE = 10

/**
 * Бейдж связей лида в header'е карточки. Два режима, приоритет у красного:
 *
 *  • КРАСНЫЙ «Дубль · N» — у лида есть другой лид с тем же телефоном И тем
 *    же объявлением Avito (item_id), созданный РАНЬШЕ. Т.е. это повторно
 *    заведённая та же заявка (совпадает с логикой антидубля). Самый ранний
 *    лид кластера «оригинал» — на нём пилюли нет.
 *  • ФИОЛЕТОВЫЙ «Есть другие лиды · N» — иначе: просто есть другие лиды с
 *    тем же телефоном (повторные обращения клиента, возможно по разным авто).
 *
 * Когда есть красный — фиолетовый не показываем (красный важнее и конкретнее).
 * Клик раскрывает список соответствующих лидов со ссылками.
 */
export function LeadRelationBadge({ lead }: { lead: InspectionLeadRow }) {
  const { t } = useTranslation()
  const phone = lead.client_phone
  const itemId = lead.item_id

  // Кластер дублей: тот же телефон + то же объявление.
  const dupQ = useQuery({
    queryKey: ['lead-same-ad', lead.id, phone, itemId],
    queryFn: () =>
      listSameAdLeads({
        phone: phone as string,
        itemId: itemId as number,
        excludeLeadId: lead.id,
      }),
    enabled: !!phone && itemId != null,
  })
  const sameAd = dupQ.data ?? []
  // Дубль = есть более ранний лид того же объявления (этот — повторный).
  const isDuplicate = sameAd.some((o) => o.created_at < lead.created_at)

  // Фиолетовый режим нужен, только если это НЕ дубль.
  const othersCountQ = useQuery({
    queryKey: ['lead-other-leads-count', lead.id, phone],
    queryFn: () =>
      countOtherLeadsByClientPhone({
        phone: phone as string,
        excludeLeadId: lead.id,
      }),
    enabled: !!phone && !dupQ.isLoading && !isDuplicate,
  })

  if (!phone) return null

  if (isDuplicate) {
    return (
      <RelationPill
        label={t('leads.relation.duplicate')}
        count={sameAd.length}
        colorClass="border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25"
        title={t('leads.relation.dupTitle', { n: sameAd.length })}
      >
        {sameAd.map((l) => (
          <LeadLink key={l.id} lead={l} accent="red" />
        ))}
      </RelationPill>
    )
  }

  const othersCount = othersCountQ.data ?? 0
  if (othersCount <= 0) return null
  return <OtherLeadsPurple leadId={lead.id} phone={phone} count={othersCount} />
}

// ── Фиолетовый режим с ленивой подгрузкой списка ────────────────────────────
function OtherLeadsPurple({
  leadId,
  phone,
  count,
}: {
  leadId: string
  phone: string
  count: number
}) {
  const { t } = useTranslation()
  const [limit, setLimit] = useState(PAGE)
  const listQ = useQuery({
    queryKey: ['lead-other-leads', leadId, phone, limit],
    queryFn: () =>
      listOtherLeadsByClientPhone({ phone, excludeLeadId: leadId, limit }),
  })
  const items = listQ.data?.items ?? []
  const total = listQ.data?.total ?? count

  return (
    <RelationPill
      label={t('leads.relation.others')}
      count={count}
      colorClass="border-purple-400/30 bg-purple-400/10 text-purple-300 hover:bg-purple-400/20"
      title={t('leads.relation.othersTitle', { n: count })}
    >
      {listQ.isLoading ? (
        <div className="px-2 py-1.5 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('common.loading')}
        </div>
      ) : listQ.isError ? (
        <div className="px-2 py-1.5 text-[length:var(--stryzh-font-size-xs)] text-red-400">
          {t('leads.relation.loadError')}
        </div>
      ) : (
        <>
          {items.map((l) => (
            <LeadLink key={l.id} lead={l} accent="purple" />
          ))}
          {total > items.length ? (
            <button
              type="button"
              onClick={() => setLimit((v) => v + PAGE)}
              className="mt-1 w-full rounded-md px-2 py-1.5 text-left text-[length:var(--stryzh-font-size-xs)] text-purple-300 hover:bg-purple-400/10"
            >
              {t('leads.relation.loadMore', { n: total - items.length })}
            </button>
          ) : null}
        </>
      )}
    </RelationPill>
  )
}

// ── Общая пилюля + поповер (кнопка, клик-наружу/Esc, прижатие к вьюпорту) ────
function RelationPill({
  label,
  count,
  colorClass,
  title,
  children,
}: {
  label: string
  count: number
  colorClass: string
  title: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [shiftX, setShiftX] = useState(0)
  const shiftXRef = useRef(0)

  const reclamp = (el: HTMLDivElement) => {
    const r = el.getBoundingClientRect()
    const naturalLeft = r.left - shiftXRef.current
    const overflowRight = naturalLeft + r.width - (window.innerWidth - 8)
    const next =
      overflowRight > 0 ? -Math.min(overflowRight, Math.max(0, naturalLeft - 8)) : 0
    if (next !== shiftXRef.current) {
      shiftXRef.current = next
      setShiftX(next)
    }
  }
  const attachPanel = (el: HTMLDivElement | null) => {
    if (el) reclamp(el)
  }
  const close = () => {
    setOpen(false)
    shiftXRef.current = 0
    setShiftX(0)
  }

  useEffect(() => {
    if (!open) return
    const onResize = () => {
      const el = rootRef.current?.querySelector<HTMLDivElement>('[data-pill-panel]')
      if (el) reclamp(el)
    }
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('resize', onResize)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('resize', onResize)
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        className={
          'inline-flex shrink-0 cursor-pointer items-center rounded-[100px] border px-2 py-0.5 ' +
          'font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] ' +
          'font-medium uppercase tracking-[0.5px] ' +
          colorClass
        }
        title={title}
      >
        {label} · {count}
      </button>
      {open ? (
        <div
          data-pill-panel
          ref={attachPanel}
          style={shiftX ? { transform: `translateX(${shiftX}px)` } : undefined}
          // Закрываем поповер при клике на ссылку лида (Link → <a>); клики по
          // кнопке «Показать ещё» (<button>) не закрывают.
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a')) close()
          }}
          className={
            'absolute left-0 top-full z-50 mt-2 w-[340px] max-w-[calc(100vw-2rem)] rounded-[var(--stryzh-radius-md)] ' +
            'border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] ' +
            'p-2 shadow-[0_12px_32px_rgba(0,0,0,0.55)]'
          }
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

function LeadLink({
  lead,
  accent,
}: {
  lead: OtherLeadRow
  accent: 'red' | 'purple'
}) {
  const { t } = useTranslation()
  const vehicle =
    [lead.vehicle_brand, lead.vehicle_model].filter(Boolean).join(' ') ||
    t('leads.relation.noVehicle')
  const leadStatus = t(`leads.status.${lead.status}`)
  const orderStatus = lead.order_status
    ? statusLabelRu(lead.order_status as OrderStatus)
    : null
  const accentCls = accent === 'red' ? 'text-red-300' : 'text-purple-300'
  return (
    <Link
      to={`/inspection-leads/${lead.id}`}
      className="block rounded-md px-2 py-1.5 hover:bg-[rgba(255,255,255,0.06)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-primary)]"
    >
      <span className="text-[color:var(--stryzh-color-text-secondary)]">
        {formatMoscowDateTime(lead.created_at)}
      </span>
      {' · '}
      {vehicle}
      {' · '}
      <span className={accentCls}>{leadStatus}</span>
      {orderStatus ? (
        <span className="text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.relation.toOrder')}
          {orderStatus}
        </span>
      ) : null}
    </Link>
  )
}
